import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Card, CardContent, CardHeader, Button, Stack, Typography, Alert, CircularProgress, useColorScheme } from "@mui/material";
import { useAuth } from "@/components/core/auth-provider";
import { Logo } from "@/components/core/logo";
import { logoColors } from "@/components/dashboard/layout/vertical/side-nav";

export function EmailVerificationConfirm(): React.JSX.Element {
	const { confirmEmailVerification } = useAuth();
	const navigate = useNavigate();

	const [searchParams] = useSearchParams();
	const { colorScheme = "light" } = useColorScheme();
	
	const [_isVerifying, setIsVerifying] = React.useState(false);
	const [verificationStatus, setVerificationStatus] = React.useState<'pending' | 'success' | 'error'>('pending');
	const [errorMessage, setErrorMessage] = React.useState<string>("");

	const logoColor = logoColors[colorScheme]["blend_in"];
	const token = searchParams.get("token");

	React.useEffect(() => {
		if (!token) {
			setVerificationStatus('error');
			setErrorMessage("Invalid verification link");
			return;
		}

		const verifyEmail = async () => {
			setIsVerifying(true);
			try {
				await confirmEmailVerification(token);
				setVerificationStatus('success');
			} catch (error: any) {
				setVerificationStatus('error');
				const errorMsg = error?.response?.data?.message;
				setErrorMessage(
					Array.isArray(errorMsg) ? errorMsg[0] : errorMsg || "Failed to verify email"
				);
			} finally {
				setIsVerifying(false);
			}
		};

		verifyEmail();
	}, []);


	const handleGoToSignIn = () => {
    localStorage.removeItem("token");
		navigate("/sign-in");
	};

	return (
		<Box
			sx={{
				width: "100%",
				height: "100vh",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				gap: 4,
				padding: 2,
			}}
		>
			<Logo height={60} width={222} color={logoColor} />
			
			<Card sx={{ maxWidth: 500, width: "100%" }}>
				<CardHeader 
					title="Email Verification" 
					subheader={
						verificationStatus === 'pending' ? "Verifying your email..." :
						verificationStatus === 'success' ? "Email verified successfully!" :
						"Verification failed"
					}
				/>
				<CardContent>
					<Stack spacing={3} alignItems="center">
						{verificationStatus === 'pending' && (
							<>
								<CircularProgress size={60} />
								<Typography variant="body2" color="text.secondary" textAlign="center">
									Please wait while we verify your email address...
								</Typography>
							</>
						)}

						{verificationStatus === 'success' && (
							<>
								<Alert severity="success" sx={{ width: "100%" }}>
									Your email has been successfully verified! You will be redirected shortly.
								</Alert>
							</>
						)}

						{verificationStatus === 'error' && (
							<>
								<Alert severity="error" sx={{ width: "100%" }}>
									{errorMessage}
								</Alert>
								<Stack direction="row" spacing={2}>
									<Button 
										variant="outlined" 
										onClick={handleGoToSignIn}
										fullWidth
									>
										Go to Sign In
									</Button>
								</Stack>
							</>
						)}
					</Stack>
				</CardContent>
			</Card>
		</Box>
	);
}
